import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  MoreActions,
  MoreActionsItem,
  OpenSidebarButton,
  OpenSidebarPayload,
  isAccess,
  isResourceAccess,
  useFeatureToggle,
  useSidebar,
} from '@css-xp/base';
import { Maybe, PaymentMethod, PaymentMethodType } from '@css-xp/graphtypes';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';
import { ENABLE_CREDIT_MANAGEMENT, ENABLE_MARK_FAVORITE, ENABLE_QUOTE } from '@atlassian/cssxp-utils-generic';
import { EditBillingDetails } from './sidebar-panels/edit-billing-details-sidebar';
import { SIDEBAR } from './transaction-account-page';
import { useFavorites } from '@css-xp/base/hooks/src/use-favourites';
import { PAGETITLE } from '@atlassian/temp-analytics';
import { FavouriteButton } from '../../../search-v2/favourites';

interface Props {
  editBillingDetailsFormProps: EditBillingDetails;
  isPartner: boolean;
  hasPaymentOnTerms: boolean;
  isActive: boolean;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  updatePaymentMethodLink?: Maybe<string>;
  transactionAccountName?: Maybe<string>;
  transactionAccountNumber?: Maybe<string>;
  creditRequestAppLink?: Maybe<string>;
  termPaymentStatus?: Maybe<string> | null;
}

const {
  BILL_TO_PRICING_ELIGIBILITY_WRITE,
  INVOICE_GROUP_WRITE,
  PAYMENT_METHOD_DEFAULT_WRITE,
  PAYMENT_METHOD_ON_TERM_WRITE,
  SHIP_TO_PARTY_WRITE,
  TRANSACTION_ACCOUNT_WRITE,
  TRANSACTION_ACCOUNT_MANAGE,
} = Permissions;

const TransactionAccountActions = ({
  editBillingDetailsFormProps,
  isPartner,
  isActive,
  availablePaymentMethods,
  updatePaymentMethodLink,
  transactionAccountName,
  transactionAccountNumber,
  creditRequestAppLink,
  termPaymentStatus,
}: Props) => {
  const { openSidebar } = useSidebar();
  const history = useHistory();
  const [isEnabled] = useFeatureToggle();
  const { isFavorited, handleFavoriteToggle } = useFavorites();
  const isMarkFavoriteEnabled = isEnabled(ENABLE_MARK_FAVORITE);
  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };
  const checkForAvailablePaymentMethods = () => {
    const cardPaymentMethod =
      availablePaymentMethods?.filter((paymentMethod: any) => paymentMethod.type === PaymentMethodType.CARD) ?? [];
    if (cardPaymentMethod?.length > 0) {
      return true;
    }
    return false;
  };
  const getEntityNameAndMetadata = () => {
    const entityName = PAGETITLE.TRANSACTION_ACCOUNT.toLowerCase().trim().replace(/\s+/g, '_');
    const metadata = {
      transactionAccountNumber: transactionAccountNumber ? transactionAccountNumber : '',
      displayName: transactionAccountName ? transactionAccountName : '',
    };
    return { entityName, metadata };
  };
  const transactionAccountActions: MoreActionsItem[] = [
    {
      id: 'Create billing profile',
      content: 'Create billing profile',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.CREATE_INVOICE_GROUP,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            billToAddress: editBillingDetailsFormProps.billToAddress,
            refetch: editBillingDetailsFormProps.onComplete,
            availablePaymentMethods: availablePaymentMethods,
            updatePaymentMethodLink: updatePaymentMethodLink,
            termPaymentStatus: termPaymentStatus,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),

      predicate: !isPartner && isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(INVOICE_GROUP_WRITE),
    },
    {
      id: 'set pricing eligibility',
      content: 'Set pricing eligibility',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.SET_PRICING_ELIGIBILITY,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            pricingEligibility: editBillingDetailsFormProps.billToPartyPricingEligibilty,
            billToAddress: editBillingDetailsFormProps.billToAddress,
            refetch: editBillingDetailsFormProps.onComplete,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate:
        true && isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(BILL_TO_PRICING_ELIGIBILITY_WRITE),
    },
    {
      id: 'create sold to address',
      content: 'Create sold-to address',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.EDIT_SHIP_TO_PARTY,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            billToAddress: editBillingDetailsFormProps.billToAddress,
            refetch: editBillingDetailsFormProps.onComplete,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),

      predicate: true && isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(SHIP_TO_PARTY_WRITE),
    },
    {
      id: 'net-30 payment terms',
      content: 'Net-30 payment terms application',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.GENERTE_NET30_PAYMENT_TERMS,
          payload: {
            creditRequestAppLink,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate:
        isEnabled(ENABLE_CREDIT_MANAGEMENT) &&
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
        isAccess(Permissions.PAYMENT_METHOD_ON_TERM_WRITE),
    },
    {
      id: 'add payment method',
      content: 'Add payment on terms',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.ADD_PAYMENT_ON_TERMS_METHOD,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            refetch: editBillingDetailsFormProps.onComplete,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),

      predicate: true && isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(PAYMENT_METHOD_ON_TERM_WRITE),
    },
    {
      id: 'set payment method',
      content: 'Set default payment method',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.SET_CARD_PAYMENT_DEFAULT,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            refetch: editBillingDetailsFormProps.onComplete,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),

      predicate:
        true &&
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
        isAccess(PAYMENT_METHOD_DEFAULT_WRITE) &&
        checkForAvailablePaymentMethods(),
    },
    {
      id: 'create quote',
      content: 'Create quote',
      action: () => history.push(`/quotes/new?txId=${editBillingDetailsFormProps.transactionAccountId}&fromTxd=true`),
      predicate: isEnabled(ENABLE_QUOTE),
    },
  ];

  return (
    <>
      {isMarkFavoriteEnabled && (
        <FavouriteButton
          isFavorited={isFavorited}
          {...getEntityNameAndMetadata()}
          handleFavoriteToggle={handleFavoriteToggle}
        />
      )}
      <div className="button-toolbar" data-testid="header-actions">
        {isAccess(TRANSACTION_ACCOUNT_MANAGE) && isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && (
          <>
            {isActive && (
              <OpenSidebarButton
                sidebar={SIDEBAR.DEACTIVATE_TRANSACTION_ACCOUNT}
                payload={{
                  ...editBillingDetailsFormProps,
                  transactionAccountName,
                  transactionAccountNumber,
                }}
                sidebarOptions={{
                  sidebarWidth: '600px',
                  isOverlay: true,
                }}
                testId="deactivate-billing-account-action"
                label="Deactivate account"
              >
                Deactivate account
              </OpenSidebarButton>
            )}
            {!isActive && (
              <OpenSidebarButton
                sidebar={SIDEBAR.ADD_BILLING_ADMINS}
                payload={{
                  ...editBillingDetailsFormProps,
                  header: 'Reactivate billing account',
                  subheader: `${transactionAccountName ?? 'Billing account'} ${transactionAccountNumber ?? ''}`,
                  transactionAccountNumber,
                  isReactivateFlow: true,
                }}
                sidebarOptions={{
                  sidebarWidth: '600px',
                  isOverlay: true,
                }}
                testId="reactivate-billing-account-action"
                label="Reactivate account"
              >
                Reactivate account
              </OpenSidebarButton>
            )}
          </>
        )}
        {isAccess(TRANSACTION_ACCOUNT_WRITE) && isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && (
          <>
            <OpenSidebarButton
              sidebar={SIDEBAR.CREATE_TRANSACTION_ACCOUNT}
              payload={{
                ...editBillingDetailsFormProps,
              }}
              sidebarOptions={{
                sidebarWidth: '600px',
                isOverlay: true,
              }}
              testId="create-transaction-account-action"
              isDisabled={!isActive}
              label="Create new"
            >
              Create new
            </OpenSidebarButton>
            <OpenSidebarButton
              sidebar={SIDEBAR.EDIT_TRANSACTION_ACCOUNT}
              payload={{
                ...editBillingDetailsFormProps,
                isPartner,
              }}
              appearance={'primary'}
              sidebarOptions={{
                sidebarWidth: '600px',
                isOverlay: true,
              }}
              testId="edit-transaction-account-action"
              isDisabled={!isActive}
              label="Edit"
            >
              Edit
            </OpenSidebarButton>
          </>
        )}
        <MoreActions actions={transactionAccountActions} />
      </div>
    </>
  );
};

export { TransactionAccountActions };
